import './App.css';
import Webcam from 'react-webcam';
import React from 'react';
import { ButtonCaptureContinue } from './ButtonCaptureContinue';
import { Gallery } from './Gallery';
import { SubmissionForm } from './SubmissionForm';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faQuestionCircle,
    faAngleDoubleRight,
    faCamera,
} from '@fortawesome/free-solid-svg-icons';
import { Hint } from './Hint';
import 'intro.js/introjs.css';
import introJs from 'intro.js';
import 'intro.js/themes/introjs-modern.css';
import { ToastContainer } from 'react-toastify';

function App() {
    const maximages = 4;
    const api = axios.create({ baseURL: process.env.REACT_APP_API });
    api.defaults.headers['Access-Control-Allow-Origin'] = '*';
    api.defaults.headers['Access-Control-Allow-Methods'] =
        'GET,PUT,POST,DELETE,PATCH,OPTIONS';
    const [hint, setHint] = React.useState(false);
    const [deviceId, setDeviceId] = React.useState(undefined);
    const [imageList, setImageList] = React.useState([]);
    const [syncBuffer, setSyncBuffer] = React.useState([]);
    const [readyToSubmit, setReadyToSubmit] = React.useState(false);
    const [highlightClass, setHighlightClass] = React.useState('red-highlight');
    const [qualityError, setQualityError] = React.useState(false);
    const [handError, setHandError] = React.useState(false);
    const [isDisabled, setIsDisabled] = React.useState(false);

    React.useEffect(() => {
        document.title = 'Capture my hand';
        setDeviceId(window.localStorage.getItem('deviceId') || undefined);
    }, []);

    const handleDevices = React.useCallback(
        (mediaDevices) => {
            const videoInputs = mediaDevices.filter(
                ({ kind }) => kind === 'videoinput'
            );
            const mainDevice =
                videoInputs.length > 1 ? videoInputs[1] : videoInputs[0];
            if (mainDevice) {
                setDeviceId(mainDevice.deviceId);
                window.localStorage.setItem('deviceId', mainDevice.deviceId);
            } else {
                console.log('No devices found');
            }
        },
        [setDeviceId]
    );

    const handleEnumerateDevices = React.useCallback(() =>
        navigator.mediaDevices.enumerateDevices().then(handleDevices)
    );

    const handleContinue = React.useCallback(() => {
        setReadyToSubmit(true);
    }, [setReadyToSubmit]);

    const handleBack = React.useCallback(() => {
        setReadyToSubmit(false);
    }, [setReadyToSubmit]);

    const handleSuccess = React.useCallback(() => {
        setImageList([]);
        setReadyToSubmit(false);
    }, [setImageList, setReadyToSubmit]);

    const handleTour = React.useCallback(() => {
        const intro = introJs();
        intro.setOptions({
            disableInteraction: true,
            steps: [
                {
                    element: '#camera',
                    intro: 'This is the camera view.',
                    position: 'bottom',
                    tooltipClass: 'introjs-modern',
                },
                {
                    element: '#button',
                    intro: 'With this button, you will take a picture.',
                    highlightClass: 'roundedHighlight',
                    tooltipClass: 'introjs-modern',
                },
                {
                    element: '#progress',
                    intro: 'Around button, you can see the progress of your photos.',
                    highlightClass: 'roundedHighlight',
                    tooltipClass: 'introjs-modern',
                },
                {
                    element: '#gallery',
                    intro: 'In this box you will see the gallery of the photos you took.',
                    tooltipClass: 'introjs-modern',
                },
                {
                    element: '#guide',
                    intro: 'This button will tell you how to take the pictures.',
                    tooltipClass: 'introjs-modern',
                },
                {
                    element: '#help',
                    intro: 'If you are lost, you can always come back here and take the tour again!',
                    tooltipClass: 'introjs-modern',
                },
            ],
        });
        intro.start();
    }, []);

    const handleHint = React.useCallback(() => {
        setHint(true);
    }, [setHint]);

    const handleHintCancel = React.useCallback(() => {
        setHint(false);
    }, [setHint]);

    const webcamRef = React.useRef(null);
    const canvasRef = React.useRef(null);
    const capture = React.useCallback(() => {
        const imageSrc = webcamRef.current.getScreenshot();
        setImageList((prev) => [...prev, imageSrc]);
    }, [webcamRef, setImageList]);

    const sendFrameToBackend = React.useCallback(async () => {
        if (webcamRef.current && canvasRef.current) {
            const imageSrc = webcamRef.current.getScreenshot();
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');
            const img = new Image();
            img.src = imageSrc;
            img.onload = async () => {
                ctx.drawImage(img, 0, 0, 225, 225);
                canvas.toBlob(async (blob) => {
                    const formData = new FormData();
                    formData.append('file', blob, 'frame.jpg');
                    try {
                        const response = await api.post('/detect/', formData, {
                            headers: {
                                'Content-Type': 'multipart/form-data',
                            },
                        });

                        setQualityError(response.data.laplacian_variance < 100);
                        setHandError(response.data.detections.length <= 0);
                        setIsDisabled(
                            response.data.detections.length <= 0 || qualityError
                        );
                    } catch (error) {
                        console.error('Error sending frame to backend:', error);
                    }
                }, 'image/jpeg');
            };
        }
    }, [webcamRef, canvasRef, api]);

    React.useEffect(() => {
        const interval = setInterval(sendFrameToBackend, 200);
        return () => clearInterval(interval);
    }, [sendFrameToBackend]);

    return (
        <>
            <div className={'camera'}>
                <Webcam
                    videoConstraints={{
                        deviceId: window.localStorage.getItem('deviceId'),
                        width: { min: 500, ideal: 3840, max: 3840 },
                        height: { min: 400, ideal: 2160, max: 2160 },
                        frameRate: { ideal: 60 },
                    }}
                    ref={webcamRef}
                    audio={false}
                    screenshotFormat="image/jpeg"
                    screenshotQuality={0.95}
                    forceScreenshotSourceSize={true}
                    onUserMedia={handleEnumerateDevices}
                    id={'camera'}
                />
                {(qualityError || handError) && (
                    <h5 className={'quality-error-message'}>
                        {qualityError && handError
                            ? 'Hand not detected and the quality is too low!'
                            : qualityError
                              ? 'Quality is too low!'
                              : 'Hand not detected!'}
                    </h5>
                )}

                <ButtonCaptureContinue
                    value={imageList.length}
                    max_value={maximages}
                    size={92}
                    thickness={8}
                    space={3}
                    continueCallback={handleContinue}
                    captureCallback={capture}
                    fontSize={24}
                    text={<FontAwesomeIcon icon={faAngleDoubleRight} />}
                    useTypeSubmit={false}
                    disabled={
                        imageList.length >= maximages ? false : isDisabled
                    }
                ></ButtonCaptureContinue>

                <div className="hint-container">
                    <button
                        className="hint-button"
                        onClick={handleTour}
                        id={'help'}
                    >
                        {' '}
                        <FontAwesomeIcon icon={faQuestionCircle} /> Intro
                    </button>
                    <button
                        id={'guide'}
                        className={'hint-button'}
                        onClick={handleHint}
                    >
                        {' '}
                        <FontAwesomeIcon icon={faCamera} /> Guide
                    </button>
                </div>

                {hint ? <Hint onHintOk={handleHintCancel} /> : <></>}

                <Gallery images={imageList} setImages={setImageList} />
                {readyToSubmit ? (
                    <>
                        <SubmissionForm
                            imageList={imageList}
                            onSuccess={handleSuccess}
                            api={api}
                            handleBack={handleBack}
                            setSyncBuffer={setSyncBuffer}
                        />
                    </>
                ) : (
                    <></>
                )}
                <canvas
                    ref={canvasRef}
                    width={225}
                    height={225}
                    style={{ display: 'none' }}
                ></canvas>
            </div>
            <ToastContainer position="top-center" theme="dark" />
        </>
    );
}

export default App;
